import React, { ReactElement, SyntheticEvent, use, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BedRounded,
  ChecklistRounded,
  NotificationImportantOutlined,
  SecurityRounded,
  VisibilityOffOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material';
import Badge from '@mui/material/Badge';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import LinkedRoomIcon from 'assets/icons/ecd-icon.svg?react';
import Constants from 'style/constants';
import { useSessionStorage } from 'usehooks-ts';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import OfficeBuildingMarker from 'shared/components/icons/office-building-marker';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import { FaultType } from 'shared/enums/fault-type';
import OrganisationUnitTreeItem from 'shared/features/organisation/components/organisation-tree-item/organisation-unit-tree-item';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import OrganisationUnitTreeProviderContext from 'shared/features/organisation/providers/contexts/organisation-unit-tree-provider-context';

import './organisation-tree.component.scss';

interface IProps {
  onSelectOrganisationUnit: (
    selectedNode: OrganisationTreeNode,
    upstreamParentBranch: OrganisationTreeNode[],
    siblings: OrganisationTreeNode[],
  ) => void;
  onDeselectOrganisationUnit: () => void;
  initialSelectedOrganisationUnit?: string;
  disableSelection?: boolean;
  showSelectionInViewMode?: boolean;
  expandedNodes: string[];
  setExpandedNodes: (updater: string[] | ((prev: string[]) => string[])) => void;
}

const OrganisationTree = (props: Readonly<IProps>): React.JSX.Element => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { organisationUnitTree, isLoading, isFetching, isSuccess, error } = use(OrganisationUnitTreeProviderContext);

  const nodesByNodeId = useMemo(() => {
    const map = new Map<string, OrganisationTreeNode>();

    if (organisationUnitTree) {
      const stack: OrganisationTreeNode[] = [...organisationUnitTree];
      while (stack.length > 0) {
        const node = stack.pop();
        if (node) {
          map.set(node.id, node);
          if (node.childOrganisationUnits && node.childOrganisationUnits.length > 0) {
            stack.push(...node.childOrganisationUnits);
          }
        }
      }
    }

    return map;
  }, [organisationUnitTree]);

  const { expandedNodes, setExpandedNodes } = props;
  const [selectedNode, setSelectedNode] = useState<string>(props.initialSelectedOrganisationUnit ?? '');
  const treeContainerRef = useRef<HTMLDivElement>(null);
  const scrolledInitialOrganisationUnitIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (organisationUnitTree) {
      const rootIds = organisationUnitTree.map((x) => x.id);
      const missingRootIds = rootIds.filter((id) => !expandedNodes.includes(id));
      if (missingRootIds.length > 0) {
        setExpandedNodes((prev) => [...prev, ...missingRootIds]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationUnitTree]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  useEffect(() => {
    initTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesByNodeId, props.initialSelectedOrganisationUnit]);

  useLayoutEffect(() => {
    const shouldScrollInitialOrganisationUnitIntoView =
      props.initialSelectedOrganisationUnit !== undefined &&
      selectedNode === props.initialSelectedOrganisationUnit &&
      isSuccess &&
      !isFetching &&
      scrolledInitialOrganisationUnitIdRef.current !== props.initialSelectedOrganisationUnit;

    if (!shouldScrollInitialOrganisationUnitIntoView) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      const selectedTreeItem = Array.from(
        treeContainerRef.current?.querySelectorAll<HTMLElement>('[data-organisation-unit-id]') ?? [],
      ).find((treeItem) => treeItem.dataset.organisationUnitId === selectedNode);

      if (selectedTreeItem) {
        selectedTreeItem.scrollIntoView({ block: 'center', inline: 'nearest' });
        scrolledInitialOrganisationUnitIdRef.current = props.initialSelectedOrganisationUnit;
      }
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [expandedNodes, isFetching, isSuccess, props.initialSelectedOrganisationUnit, selectedNode]);

  const initTree = (): void => {
    let initialSelectedNode = selectedNode;
    if (props.initialSelectedOrganisationUnit) {
      setSelectedNode(props.initialSelectedOrganisationUnit);
      initialSelectedNode = props.initialSelectedOrganisationUnit;
    }

    if (initialSelectedNode) {
      const selectedOrganisationTreeNode = nodesByNodeId.get(initialSelectedNode);
      if (selectedOrganisationTreeNode) {
        const allParentNodes = createUpstreamParentBranch(selectedOrganisationTreeNode, [selectedOrganisationTreeNode]);

        setExpandedNodes((prev) => {
          const toAdd = [initialSelectedNode, ...allParentNodes.map((node) => node.id)].filter((id) => !prev.includes(id));
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });

        props.onSelectOrganisationUnit(selectedOrganisationTreeNode, allParentNodes, createSiblingsList(selectedOrganisationTreeNode));
      } else {
        props.onDeselectOrganisationUnit();
      }
    }
  };

  const handleToggle = (event: React.SyntheticEvent | null, nodeIds: string[]): void => {
    event?.persist();
    if (event?.target instanceof SVGElement || (event?.target as HTMLElement).className === 'MuiTreeItem-iconContainer') {
      setExpandedNodes(nodeIds.filter((id) => nodesByNodeId.has(id)));
    }
  };

  const handleSelect = (event: SyntheticEvent<Element, Event> | null, itemId: string | null): void => {
    event?.persist();
    if (event?.target instanceof HTMLElement && event?.target.className !== 'MuiTreeItem-iconContainer' && itemId) {
      if (nodesByNodeId.has(itemId)) {
        confirmSelect(itemId);
      }
    }
  };

  const confirmSelect = (nodeId: string): void => {
    if (nodeId === selectedNode) {
      onDeselect();
    } else {
      onSelect(nodeId);
    }
  };

  const onDeselect = (): void => {
    props.onDeselectOrganisationUnit();
    setSelectedNode('');
  };

  const onSelect = (nodeId: string): void => {
    const selectedNode = nodesByNodeId.get(nodeId);
    if (selectedNode) {
      props.onSelectOrganisationUnit(
        selectedNode,
        createUpstreamParentBranch(selectedNode, [selectedNode]),
        createSiblingsList(selectedNode),
      );
    }
    setSelectedNode(nodeId);
  };

  const createSiblingsList = (node: OrganisationTreeNode): OrganisationTreeNode[] => {
    if (node.parentId === undefined || !nodesByNodeId.has(node.parentId)) {
      return [];
    }

    const parent = nodesByNodeId.get(node.parentId);
    if (parent) {
      const indexOfSelectedNode = parent.childOrganisationUnits.indexOf(node);
      const copyOfChildOrganisationUnits = [...parent.childOrganisationUnits];
      copyOfChildOrganisationUnits.splice(indexOfSelectedNode, 1);
      return copyOfChildOrganisationUnits;
    }

    return [];
  };

  const createUpstreamParentBranch = (
    node?: OrganisationTreeNode,
    upstreamParentBranch: OrganisationTreeNode[] = [],
  ): OrganisationTreeNode[] => {
    if (node?.parentId === undefined || !nodesByNodeId.has(node.parentId)) {
      return upstreamParentBranch;
    }

    const parent = nodesByNodeId.get(node.parentId);
    if (parent) {
      upstreamParentBranch.push(parent);
    }

    return createUpstreamParentBranch(parent, upstreamParentBranch);
  };

  const errorFaultTypes = [FaultType.Offline, FaultType.BatteryCritical, FaultType.OutDated];

  const smallIconTheme = (isVisible: boolean): SxProps<Theme> => ({
    fontSize: 16,
    color: isVisible ? 'black' : 'transparent',
  });

  const largeFaultsIconTheme = (faults: FaultType[]): SxProps<Theme> => ({
    fontSize: 24,
    color: faults.length === 0 ? 'transparent' : Constants.Colors.onSurface,
  });

  const badgeTheme = (faults: FaultType[]): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    margin: '0px 16px',
    '.MuiBadge-badge': {
      marginRight: `-${(faults.length.toString().length - 1) * 4}px`,
      marginBottom: '4px',
      color: Constants.Colors.onInverseSurface,
      bgcolor: faults.some((fault) => errorFaultTypes.includes(fault)) ? Constants.Colors.error : Constants.Colors.brightOrange,
    },
  });

  const renderStartingIcons = (node: OrganisationTreeNode): ReactElement => (
    <div className={'starting-icons'}>{node.isLocation && <OfficeBuildingMarker sx={smallIconTheme(node.isLocation)} />}</div>
  );

  const getLinkedRoomIconClass = (node: OrganisationTreeNode): string => {
    if (node.electronicClientRecordExistsRemotely !== null) {
      return node.electronicClientRecordExistsRemotely ? 'active' : 'failed';
    }

    return 'inactive';
  };

  const renderEndingIcons = (node: OrganisationTreeNode): ReactElement => (
    <div className={'ending-icons'}>
      <ChecklistRounded sx={smallIconTheme(node.isSubscriptionChannel)} />
      <BedRounded sx={smallIconTheme(node.isResidentialLocation)} />
      <VisibilityOffOutlined sx={smallIconTheme(node.isHidden)} />
      <LinkedRoomIcon className={`linkedRoomIcon ${getLinkedRoomIconClass(node)}`} />
      <NotificationImportantOutlined sx={smallIconTheme(node.hasAlternativeDiversionOrganisationUnits)} />
      <SecurityRounded sx={smallIconTheme(node.hasAlternativeEmergencyAlarmRouteOrganisationUnits)} />
      <Badge
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={badgeTheme(node.faults)}
        badgeContent={node.faults.length}
      >
        <WarningAmberRounded sx={largeFaultsIconTheme(node.faults)} />
      </Badge>
    </div>
  );

  const renderChildNodes = (node: OrganisationTreeNode): ReactElement[] | undefined => {
    return node.childOrganisationUnits?.map((child) => (
      <OrganisationUnitTreeItem
        key={child.id}
        itemId={child.id}
        data-organisation-unit-id={child.id}
        labelText={child.name}
        disableSelection={props.disableSelection ?? false}
        showSelectionInViewMode={props.showSelectionInViewMode}
        startingIcons={renderStartingIcons(child)}
        endingIcons={renderEndingIcons(child)}
        isRoot={false}
        data-testid={`organisationTreeItem-${child.id}`}
      >
        {renderChildNodes(child)}
      </OrganisationUnitTreeItem>
    ));
  };

  if (isLoading || isFetching) {
    return <LoadingIndicator />;
  } else if (isSuccess && organisationUnitTree) {
    return (
      <div ref={treeContainerRef} className="tree-view-container" data-testid="organisationTree">
        <SimpleTreeView
          disableSelection={props.disableSelection}
          aria-label="rich object"
          className="tree-view"
          onExpandedItemsChange={handleToggle}
          onSelectedItemsChange={handleSelect}
          expandedItems={expandedNodes}
          selectedItems={selectedNode}
        >
          {organisationUnitTree.map((rootItem) => (
            <OrganisationUnitTreeItem
              key={rootItem.id}
              itemId={rootItem.id}
              data-organisation-unit-id={rootItem.id}
              labelText={rootItem.name}
              disableSelection={props.disableSelection ?? false}
              showSelectionInViewMode={props.showSelectionInViewMode}
              startingIcons={renderStartingIcons(rootItem)}
              endingIcons={renderEndingIcons(rootItem)}
              isRoot={false}
              data-testid={`organisationTreeItem-${rootItem.id}`}
            >
              {renderChildNodes(rootItem)}
            </OrganisationUnitTreeItem>
          ))}
        </SimpleTreeView>
      </div>
    );
  } else {
    return <></>;
  }
};

interface IOrganisationTreeProps {
  onSelectOrganisationUnit: (
    selectedNode: OrganisationTreeNode,
    upstreamParentBranch: OrganisationTreeNode[],
    siblings: OrganisationTreeNode[],
  ) => void;
  onDeselectOrganisationUnit: () => void;
  initialSelectedOrganisationUnit?: string;
  disableSelection?: boolean;
  showSelectionInViewMode?: boolean;
  sessionStorageKey?: string;
}

const OrganisationTreeWithSessionStorage = (
  props: Readonly<Omit<IOrganisationTreeProps, 'sessionStorageKey'> & { sessionStorageKey: string }>,
): React.JSX.Element => {
  const [expandedNodes, setExpandedNodes] = useSessionStorage<string[]>(props.sessionStorageKey, []);
  return <OrganisationTree {...props} expandedNodes={expandedNodes} setExpandedNodes={setExpandedNodes} />;
};

const OrganisationTreeInMemory = (props: Readonly<Omit<IOrganisationTreeProps, 'sessionStorageKey'>>): React.JSX.Element => {
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  return <OrganisationTree {...props} expandedNodes={expandedNodes} setExpandedNodes={setExpandedNodes} />;
};

const OrganisationTreeRouter = (props: Readonly<IOrganisationTreeProps>): React.JSX.Element => {
  if (props.sessionStorageKey) {
    return <OrganisationTreeWithSessionStorage {...props} sessionStorageKey={props.sessionStorageKey} />;
  }
  return <OrganisationTreeInMemory {...props} />;
};

export default OrganisationTreeRouter;
